import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const HOUR_MS = 60 * 60 * 1000;

/**
 * Per-account feature `enable_lead_response_watch` from config/features.yml, toggled in
 * Super Admin on the account (same checklist as lead_quality, contact_summary, etc.).
 * @param {import('vuex').Store} store
 * @param {number|string|null|undefined} accountId
 */
export function isLeadResponseWatchEnabledForAccount(store, accountId) {
  if (!store || accountId == null || accountId === '') return false;
  return store.getters['accounts/isFeatureEnabledonAccount'](
    Number(accountId),
    FEATURE_FLAGS.ENABLE_LEAD_RESPONSE_WATCH
  );
}

/**
 * Last message is a real human agent turn (outgoing / template).
 * Automation-sent messages, bot messages, and campaign messages do NOT count.
 * Matches server-side Message#human_response? logic.
 * @param {object|null|undefined} message
 */
export function isConversationLastTurnAgentPublic(message) {
  if (!message) return false;
  if (message.private) return false;
  const t = message.message_type;
  const isOutgoingType =
    t === MESSAGE_TYPE.OUTGOING ||
    t === MESSAGE_TYPE.TEMPLATE ||
    t === 1 ||
    t === 3;
  if (!isOutgoingType) return false;

  // Exclude automation-sent messages (content_attributes.automation_rule_id present)
  const contentAttrs = message.content_attributes || {};
  if (contentAttrs.automation_rule_id) return false;

  // Exclude campaign messages (additional_attributes.campaign_id present)
  const additionalAttrs = message.additional_attributes || {};
  if (additionalAttrs.campaign_id) return false;

  // Exclude bot messages (sender_type is AgentBot)
  if (message.sender_type === 'AgentBot') return false;

  return true;
}

/**
 * Pick last non-activity message from a conversation payload.
 * Compares both the store messages array and the API last_non_activity_message
 * field so that newly added messages (via ActionCable) are always preferred
 * over the stale API snapshot. This mirrors the logic in conversations.js mixin.
 * @param {object|null|undefined} conversation
 */
export function lastNonActivityMessageFromConversation(conversation) {
  if (!conversation) return null;

  const list = conversation.messages || [];
  const nonAct = list.filter(m => m.message_type !== MESSAGE_TYPE.ACTIVITY);
  const fromStore = nonAct.length ? nonAct[nonAct.length - 1] : null;
  const fromApi = conversation.last_non_activity_message || null;

  if (fromStore && fromApi) {
    // Prefer whichever is more recent
    return Number(fromStore.created_at) >= Number(fromApi.created_at)
      ? fromStore
      : fromApi;
  }

  return fromStore || fromApi || null;
}

/**
 * Returns true when a WhatsApp outgoing message has not yet been confirmed by WhatsApp
 * (no source_id assigned, and not still uploading/in-progress).
 * This means the message is stuck and was NOT actually delivered to WhatsApp.
 * @param {object} message
 * @param {object|null|undefined} conversation
 * @param {string|null|undefined} [channelTypeOverride] - explicit channel type (e.g. from inboxes store)
 */
function isUnconfirmedWhatsAppOutgoing(
  message,
  conversation,
  channelTypeOverride = null
) {
  // Prefer the explicit override (looked up from the inboxes store by the caller)
  // because conversation.meta.channel can be clobbered by ActionCable UPDATE_CONVERSATION
  // payloads where push_meta does not include `channel`. Fall back to the two known
  // locations conversation payloads may carry the channel in.
  const channelType =
    channelTypeOverride ||
    conversation?.meta?.channel ||
    conversation?.channel;
  if (channelType !== INBOX_TYPES.WHATSAPP) return false;

  const hasSourceId = !!message.source_id;
  const isInProgress = message.status === 'progress';

  return !hasSourceId && !isInProgress;
}

/**
 * When the last visible turn is a public agent message, treat as "attended" for the watch
 * even if `waiting_since` is still set (e.g. automation/bot/API sends that do not clear it server-side).
 * Exception: a WhatsApp outgoing message with no source_id is unconfirmed — treat as NOT attended
 * so the clock turns red immediately.
 * Only open and pending conversations are considered — resolved and snoozed always return null (grey).
 * @param {object|null|undefined} conversation
 * @param {object|null|undefined} [lastMessage] - optional precomputed last non-activity message
 * @param {{ store: import('vuex').Store, accountId: number|string }} [featureGate] - required for gating; when omitted, returns null
 */
export function effectiveWaitingSinceUnixForConversation(
  conversation,
  lastMessage = null,
  channelType = null,
  featureGate = null
) {
  if (
    !featureGate?.store ||
    featureGate.accountId == null ||
    featureGate.accountId === '' ||
    !isLeadResponseWatchEnabledForAccount(
      featureGate.store,
      featureGate.accountId
    )
  ) {
    return null;
  }

  // Only show watch for open or pending conversations
  const status = conversation?.status;
  if (status !== 'open' && status !== 'pending') return null;

  const last =
    lastMessage || lastNonActivityMessageFromConversation(conversation);

  // No real message exists at all — conversation was just created with no
  // incoming or outgoing messages yet. waiting_since = created_at by default,
  // but there is nothing to wait for, so show grey.
  if (!last) return null;

  // Last message is a real human agent reply — attended, show grey.
  // Exception: WhatsApp outgoing with no source_id is unconfirmed (stuck),
  // force the clock red immediately regardless of how long ago it was sent.
  if (isConversationLastTurnAgentPublic(last)) {
    if (!isUnconfirmedWhatsAppOutgoing(last, conversation, channelType)) {
      return null;
    }
    return { unix: Number(last.created_at || 0), forceRed: true };
  }

  // Last message must be an incoming message to warrant showing the watch.
  // If the last non-activity message is outgoing (automation/bot/campaign —
  // already excluded above as human) but somehow reached here, treat as grey.
  const lastType = last.message_type;
  const isIncoming = lastType === MESSAGE_TYPE.INCOMING || lastType === 0;
  if (!isIncoming) return null;

  const ws = conversation?.waiting_since;
  if (ws == null || ws === '' || Number(ws) <= 0) return null;
  return Number(ws);
}

/**
 * @param {number|string|null|undefined} waitingSinceUnix - UTC epoch seconds when wait started (open conversation waiting_since)
 * @param {number} [nowMs=Date.now()] - current time in ms (injectable for tests)
 * @returns {'grey'|'orange'|'red'}
 */
export function leadResponseWatchLevel(waitingSinceUnix, nowMs = Date.now()) {
  if (
    waitingSinceUnix == null ||
    waitingSinceUnix === '' ||
    Number(waitingSinceUnix) <= 0
  ) {
    return 'grey';
  }
  const waitedMs = nowMs - Number(waitingSinceUnix) * 1000;
  if (waitedMs < 0) return 'grey';
  if (waitedMs > 2 * HOUR_MS) return 'red';
  if (waitedMs > 1 * HOUR_MS) return 'orange';
  return 'grey';
}
